"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import type { UIMessage } from "ai";
import type { ReactNode } from "react";
import { AlertCircle, LogIn, RotateCcw } from "lucide-react";
import { useAuth } from "@/lib/auth/context";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { PROVIDER_UNAVAILABLE_CODE, PROVIDER_UNAVAILABLE_MESSAGE } from "@/lib/chat/provider-errors";
interface ChatMessageListProps {
  messages: UIMessage[];
  isLoading: boolean;
  /** Error from the chat API — displayed as an inline error banner */
  error?: Error | null;
  /** Reset the chat session (used when error requires starting fresh) */
  onReset?: () => void;
  /** Quick action buttons — rendered below messages, hidden during loading */
  quickActions?: ReactNode;
}
const LOGIN_RECOVERY_MESSAGE = "Bitte melden Sie sich erneut an, um dieses Gespräch fortzusetzen. Ihr Gespräch bleibt erhalten.";
function requiresConversationLogin(error: Error): boolean {
  let message = error.message;
  try {
    const body = JSON.parse(message);
    if (typeof body?.error === "string") message = body.error;
  } catch {
    // The SDK also surfaces errors as plain text.
  }
  return /^Conversation access denied$/i.test(message.trim());
}

/**
 * Map API error messages to user-friendly German text.
 * Falls back to a generic message for unknown errors.
 */
function getErrorMessage(error: Error): string {
  if (requiresConversationLogin(error)) return LOGIN_RECOVERY_MESSAGE;
  const msg = error.message;

  // Try to parse JSON error body (AI SDK wraps it as text)
  try {
    const parsed = JSON.parse(msg);
    // The CODE wins over the prose, and that ordering is the contract: the
    // server's `error` string exists for the transcript row it also writes and
    // as a fallback for any client that does not know the code, but what the
    // user reads is decided here, where UI copy belongs.
    //
    // Matching a code at all is the fix. The provider being unreachable is not
    // something a user can clear by retrying, and "bitte versuchen Sie es
    // erneut" — the catch-all below — is exactly what 16 people were told
    // during the 2026-09-02…04 Anthropic billing outage.
    if (parsed.code === PROVIDER_UNAVAILABLE_CODE) return PROVIDER_UNAVAILABLE_MESSAGE;
    if (parsed.error) {
      const apiError = parsed.error as string;
      if (apiError.includes("not found")) return "Die Sitzung konnte nicht gefunden werden. Bitte starten Sie einen neuen Chat.";
      if (apiError.includes("maximale Nachrichtenanzahl")) return apiError; // Already in German
      if (apiError.includes("Verification")) return "Die Sicherheitsüberprüfung ist fehlgeschlagen. Bitte versuchen Sie es erneut.";
      return apiError;
    }
  } catch {
    // Not JSON — try matching raw text
  }
  if (msg.includes("not found")) return "Die Sitzung konnte nicht gefunden werden. Bitte starten Sie einen neuen Chat.";
  if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) return "Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.";
  return "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder starten Sie einen neuen Chat.";
}

/**
 * Scrollable message list with auto-scroll to bottom on new messages.
 * Quick actions appear below the latest message and update on step change.
 */
export function ChatMessageList(t0) {
  const $ = _c(28);
  const {
    messages,
    isLoading,
    error,
    onReset,
    quickActions
  } = t0;
  const bottomRef = useRef(null);
  const {
    openLoginModal
  } = useAuth();
  let t1;
  if ($[0] !== error) {
    t1 = !!error && requiresConversationLogin(error);
    $[0] = error;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const requiresLogin = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      bottomRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== error || $[4] !== isLoading || $[5] !== messages || $[6] !== quickActions) {
    t3 = [messages, isLoading, error, quickActions];
    $[3] = error;
    $[4] = isLoading;
    $[5] = messages;
    $[6] = quickActions;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== messages) {
    t4 = messages.map(_temp);
    $[8] = messages;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isLoading) {
    t5 = isLoading && <div className="flex justify-start mb-3"><div className="glass-light text-on-surface-variant rounded-2xl rounded-tl-none px-4 py-2.5 text-sm"><span className="inline-flex items-center gap-1"><span className="animate-pulse">●</span><span className="animate-pulse" style={{
            animationDelay: "0.2s"
          }}>●</span><span className="animate-pulse" style={{
            animationDelay: "0.4s"
          }}>●</span></span></div></div>;
    $[10] = isLoading;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== error || $[13] !== isLoading || $[14] !== onReset || $[15] !== openLoginModal || $[16] !== requiresLogin) {
    t6 = !isLoading && error && <div className="flex justify-start mb-3"><div className="bg-error-container border border-error/20 text-error rounded-2xl rounded-tl-none px-4 py-3 text-sm max-w-[calc(85%-36px)]"><div className="flex items-start gap-2"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><p>{getErrorMessage(error)}</p></div>{requiresLogin ? <button onClick={() => openLoginModal()} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-error hover:text-error/80 transition-colors"><LogIn className="h-3 w-3" />Anmelden</button> : onReset && <button onClick={onReset} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-error hover:text-error/80 transition-colors"><RotateCcw className="h-3 w-3" />Neuen Chat starten</button>}</div></div>;
    $[12] = error;
    $[13] = isLoading;
    $[14] = onReset;
    $[15] = openLoginModal;
    $[16] = requiresLogin;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== error || $[19] !== isLoading || $[20] !== quickActions) {
    t7 = !isLoading && !error && quickActions && <div className="mt-2">{quickActions}</div>;
    $[18] = error;
    $[19] = isLoading;
    $[20] = quickActions;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div ref={bottomRef} />;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t9 = <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">{t4}{t5}{t6}{t7}{t8}</div>;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
}
function _temp(message) {
  return <ChatMessage key={message.id} message={message} />;
}